import React, { useState } from 'react';
import { useScrollReveal } from '@/lib/useScrollReveal';
import clsx from 'clsx';

export const CTASection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Simulate API call
    setSubmitted(true);
  };

  return (
    <section
      className="relative py-24 bg-primary overflow-hidden"
      ref={ref}
    >
      {/* Radial gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(129, 140, 248, 0.3) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div
          className={clsx(
            'transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ready to give your agents a memory?
          </h2>
          <p className="text-xl text-indigo-100 mb-10 max-w-xl mx-auto">
            Get your free API key in minutes. No credit card required.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="you@company.com"
                  className={clsx(
                    'flex-1 px-4 py-3 rounded-lg bg-white text-text-primary placeholder-text-muted',
                    'border focus:outline-none focus:ring-2 focus:ring-white/50 transition-all',
                    error
                      ? 'border-danger focus:border-danger'
                      : 'border-transparent focus:border-white'
                  )}
                  aria-label="Email address"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-indigo-50 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  Get API Key
                </button>
              </div>
              {error && (
                <p className="mt-3 text-sm text-red-200 text-left">{error}</p>
              )}
            </form>
          ) : (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md mx-auto">
              <div className="text-white font-medium text-lg mb-1">
                Check your email for your API key!
              </div>
              <p className="text-indigo-200 text-sm">
                We've sent your API key to {email}
              </p>
            </div>
          )}

          <p className="mt-6 text-indigo-200 text-sm">
            Free tier includes 1K ops/mo. No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CTASection;